/**
 * Formule de prix unifiée — utilisée par PriceCalculator (modifier),
 * InventaireDetailDialog (aperçu) et le PDF (côté backend, équivalent Python).
 *
 * Source unique de vérité : la formule HT paramétrée dans Admin > Paramètres.
 */

function escapeRegex(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function buildVariableMap(d, infos, fourns, fournituresActive) {
    const ib = infos || {};
    const f = fourns || {};
    // Quand "Non" est sélectionné, toutes les fournitures cartons/adhésif/bulle
    // sont forcées à 0 (peu importe les valeurs résiduelles en base).
    // Seul "Autre fourniture" reste utilisable.
    const noFournitures = fournituresActive === false;
    return {
        'Camion 20 m³': parseFloat(ib.camion_20) || 0,
        'Camion 30 m³': parseFloat(ib.camion_30) || 0,
        'Distance': parseFloat(ib.distance) || 0,
        'Cartons std': noFournitures ? 0 : (parseFloat(f.cartons_standards) || 0),
        'Cartons livres': noFournitures ? 0 : (parseFloat(f.cartons_livres) || 0),
        'Cartons verres': noFournitures ? 0 : (parseFloat(f.cartons_verres) || 0),
        'Cartons bout': noFournitures ? 0 : (parseFloat(f.cartons_bouteilles) || 0),
        'Adhésif': noFournitures ? 0 : (parseFloat(f.adhesif) || 0),
        'Bulle 100m x 50cm': noFournitures ? 0 : (parseFloat(f.bulle_grande) || 0),
        'Bulle 10m x 1m': noFournitures ? 0 : (parseFloat(f.bulle_petite) || 0),
        'Autre fourniture': parseFloat(f.autres) || 0,
        'Tarif déménageur': parseFloat(d.tarif_demenageur) || 0,
        'Panier': parseFloat(d.panier) || 0,
        'Hôtel': parseFloat(d.hotel) || 0,
        'Prix AOT départ': parseFloat(d.prix_aot_depart) || 0,
        'Prix AOT arrivée': parseFloat(d.prix_aot_arrivee) || 0,
        'Prix Monte-meuble départ': parseFloat(d.prix_monte_meuble_depart) || 0,
        'Prix Monte-meuble arrivée': parseFloat(d.prix_monte_meuble_arrivee) || 0,
        'Tarif déchetterie': parseFloat(d.tarif_dechetterie) || 0,
        'Tarif objet lourd': parseFloat(d.tarif_objet_lourd) || 0,
        'Marge': parseFloat(d.marge) || 0,
    };
}

export function evaluateFormula(formulaStr, values) {
    if (!formulaStr) return NaN;
    const keys = Object.keys(values).sort((a, b) => b.length - a.length);
    let expr = formulaStr;
    for (const k of keys) {
        const re = new RegExp(escapeRegex(k), 'g');
        expr = expr.replace(re, `(${values[k]})`);
    }
    try {
        // eslint-disable-next-line no-new-func
        const result = new Function('return (' + expr + ')')();
        if (typeof result !== 'number' || !isFinite(result)) return NaN;
        return result;
    } catch {
        return NaN;
    }
}

export function calcTotals(d, visibleSharedKeys, infos, fourns, formulaStr, tvaRate, fournituresActive, options) {
    if (!d) return { ht: 0, ttc: 0, complete: false };
    const nb = d.nb_jours;
    if (!nb || nb === '') return { ht: 0, ttc: 0, complete: false };
    const jours = parseInt(nb) || 0;
    if (jours <= 0) return { ht: 0, ttc: 0, complete: false };
    const ej = d.effectif_jours || {};
    for (let i = 1; i <= jours; i++) {
        if (!ej[String(i)] || ej[String(i)] === '') return { ht: 0, ttc: 0, complete: false };
    }
    if (d.tarif_demenageur === '' || d.tarif_demenageur === undefined || d.tarif_demenageur === null) return { ht: 0, ttc: 0, complete: false };
    if (d.panier === '' || d.panier === undefined || d.panier === null) return { ht: 0, ttc: 0, complete: false };
    if (d.hotel === '' || d.hotel === undefined || d.hotel === null) return { ht: 0, ttc: 0, complete: false };
    if (d.marge === '' || d.marge === undefined || d.marge === null) return { ht: 0, ttc: 0, complete: false };
    // Les "shared values" sont saisies une seule fois et appliquées à toutes les
    // formules. On les fusionne dans `d` avant validation/évaluation.
    const sharedVals = options?.sharedValues || {};
    const dMerged = { ...d };
    for (const k of Object.keys(sharedVals)) {
        const sv = sharedVals[k];
        if (sv !== undefined && sv !== null && sv !== '') {
            dMerged[k] = sv;
        }
    }
    for (const key of (visibleSharedKeys || [])) {
        if (dMerged[key] === '' || dMerged[key] === undefined || dMerged[key] === null) return { ht: 0, ttc: 0, complete: false };
    }
    // Conditions sur tarifs detectes depuis le calculateur de volume
    const presenceDechetterie = options?.presenceDechetterie === true;
    const presenceObjetLourd = options?.presenceObjetLourd === true;
    if (presenceDechetterie) {
        if (dMerged.tarif_dechetterie === '' || dMerged.tarif_dechetterie === undefined || dMerged.tarif_dechetterie === null) {
            return { ht: 0, ttc: 0, complete: false };
        }
    }
    if (presenceObjetLourd) {
        if (dMerged.tarif_objet_lourd === '' || dMerged.tarif_objet_lourd === undefined || dMerged.tarif_objet_lourd === null) {
            return { ht: 0, ttc: 0, complete: false };
        }
    }
    const values = buildVariableMap(dMerged, infos, fourns, fournituresActive);
    // Si les conditions ne sont pas remplies, on neutralise leur impact dans la formule
    if (!presenceDechetterie) values['Tarif déchetterie'] = 0;
    if (!presenceObjetLourd) values['Tarif objet lourd'] = 0;
    // AOT / Monte-meuble : neutralise les prix résiduels quand la case de
    // l'adresse est décochée (la ligne n'est plus visible mais une ancienne
    // valeur peut rester en base et gonfler le total).
    const AOT_MM_VARS = {
        prix_aot_depart: 'Prix AOT départ',
        prix_aot_arrivee: 'Prix AOT arrivée',
        prix_monte_meuble_depart: 'Prix Monte-meuble départ',
        prix_monte_meuble_arrivee: 'Prix Monte-meuble arrivée',
    };
    const visibles = new Set(visibleSharedKeys || []);
    for (const [k, varName] of Object.entries(AOT_MM_VARS)) {
        if (!visibles.has(k)) values[varName] = 0;
    }
    const ht = evaluateFormula(formulaStr, values);
    if (isNaN(ht)) return { ht: 0, ttc: 0, complete: false };
    const tva = (typeof tvaRate === 'number' ? tvaRate : 20) / 100;
    return {
        ht: Math.round(ht * 100) / 100,
        ttc: Math.round(ht * (1 + tva) * 100) / 100,
        complete: true,
    };
}

/**
 * Calcule les valeurs partagées (AOT/MM/Tarif déchet./Tarif objet lourd) en
 * prenant la 1ère valeur non vide trouvée parmi toutes les formules.
 * Cette fonction permet de garantir qu'une saisie unique est appliquée à
 * toutes les formules, même pour les anciens inventaires où la propagation
 * n'avait pas été faite à l'enregistrement.
 */
export function computeSharedValues(formulesData, sharedKeyList) {
    const out = {};
    const formules = Object.keys(formulesData || {});
    for (const key of (sharedKeyList || [])) {
        for (const fk of formules) {
            const v = (formulesData[fk] || {})[key];
            if (v !== undefined && v !== null && v !== '') {
                out[key] = v;
                break;
            }
        }
    }
    return out;
}
